<template>
  <label class="ui-field" :class="{ 'ui-field--disabled': disabled, 'ui-field--error': error }">
    <span v-if="label" class="ui-field__label">
      {{ label }}
      <span v-if="required">*</span>
    </span>
    <span class="ui-input">
      <hs-icon v-if="icon" :name="icon" :size="16" class="ui-input__icon" />
      <input
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :maxlength="maxlength"
        @input="$emit('update:modelValue', $event.target.value)"
        @change="$emit('change', $event)"
        @keyup="$emit('keyup', $event)"
      />
    </span>
    <span v-if="hint && !error" class="ui-field__hint">{{ hint }}</span>
    <span v-if="error" class="ui-field__error">{{ error }}</span>
  </label>
</template>

<script>
import HsIcon from "./HsIcon.vue";

export default {
  name: "HsInput",
  components: { HsIcon },
  props: {
    modelValue: {
      type: [String, Number],
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    hint: {
      type: String,
      default: "",
    },
    error: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: "off",
    },
    maxlength: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["update:modelValue", "change", "keyup"],
};
</script>

<style scoped>
.ui-field {
  display: grid;
  gap: 0.42rem;
  min-width: 0;
}

.ui-field__label {
  color: var(--hs-text, #0f172a);
  font-size: 0.82rem;
  font-weight: 950;
}

.ui-field__label span,
.ui-field__error {
  color: var(--hs-danger, #b91c1c);
}

.ui-input {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 72%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 15px;
  display: flex;
  gap: 0.6rem;
  min-height: 2.75rem;
  padding: 0 0.85rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.ui-input:focus-within {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 46%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
}

.ui-input__icon {
  color: var(--hs-muted, #64748b);
}

.ui-input input {
  background: transparent;
  border: 0;
  color: var(--hs-text, #0f172a);
  flex: 1;
  font-size: 0.9rem;
  font-weight: 800;
  min-width: 0;
  outline: none;
}

.ui-input input::placeholder {
  color: color-mix(in srgb, var(--hs-muted, #64748b) 70%, transparent);
}

.ui-field__hint,
.ui-field__error {
  font-size: 0.76rem;
  font-weight: 800;
}

.ui-field__hint {
  color: var(--hs-muted, #64748b);
}

.ui-field--error .ui-input {
  border-color: color-mix(in srgb, var(--hs-danger, #b91c1c) 55%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--hs-danger, #b91c1c) 10%, transparent);
}

.ui-field--disabled {
  opacity: 0.62;
}
</style>
